import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { deleteSeedVarietyProduct, fetchSeedVarietiesAdmin, getToken } from './api';

function emptyBlock() {
  const d = DEFAULT_SITE.seedVarieties;
  return {
    categories: Array.isArray(d?.categories) ? [...d.categories] : [],
    families: Array.isArray(d?.families) ? [...d.families] : [],
  };
}

export default function SeedVarietiesDashboard() {
  const navigate = useNavigate();
  const [block, setBlock] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSeedVarietiesAdmin()
      .then((b) => setBlock(b && typeof b === 'object' ? b : emptyBlock()))
      .catch(() => setBlock(emptyBlock()));
  }, [navigate]);

  async function removeProduct(prod) {
    if (!window.confirm(`Delete “${prod.familyLabel || prod.categoryName || prod.id}” and all its variants?`)) return;
    setMsg('');
    setErr('');
    setBusy(true);
    try {
      const next = await deleteSeedVarietyProduct(prod.id);
      setBlock(next && typeof next === 'object' ? next : emptyBlock());
      setMsg('Product removed.');
    } catch (e) {
      setErr(e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  if (!block) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  const { families } = block;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <p className="text-xs font-medium text-neutral-500">Site content</p>
          <h2 className="text-lg font-bold text-neutral-900 md:text-xl">Variety products</h2>
          {/* <p className="mt-1 w-full max-w-none text-xs text-neutral-500">
            Technical variety sheets — separate from main catalog products.
          </p> */}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* <Link
            to="/admin/dashboard/seed-varieties/categories"
            className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-800 hover:bg-neutral-50"
          >
            Categories
          </Link> */}
          <Link
            to="/admin/dashboard/seed-varieties/products/new"
            className="rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            + New product
          </Link>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-semibold text-neutral-800">Products</h3>
          {/* <p className="mt-1 text-sm text-neutral-600">
            Add <Link to="/admin/dashboard/seed-varieties/categories" className="font-medium text-brand-olive hover:underline">categories</Link> first, then products. Each product holds variants with images and specification rows.
          </p> */}

          {families.length === 0 ? (
            <p className="mt-6 text-center text-sm text-neutral-500">
              No products yet.{' '}
              <Link to="/admin/dashboard/seed-varieties/categories/new" className="font-medium text-brand-olive hover:underline">
                Create a category
              </Link>{' '}
              then{' '}
              <Link to="/admin/dashboard/seed-varieties/products/new" className="font-medium text-brand-olive hover:underline">
                add a product
              </Link>
              .
            </p>
          ) : (
            <div className="mt-6 overflow-x-auto rounded-xl border border-neutral-200">
              <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Title / category
                    </th>
                    <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">URL id</th>
                    <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Variants
                    </th>
                    <th className="w-[1%] whitespace-nowrap px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {families.map((f) => (
                    <tr key={f.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/90">
                      <td className="px-3 py-2 align-middle font-medium text-neutral-900">
                        <span className="block">{f.familyLabel?.trim() || f.categoryName?.trim() || '—'}</span>
                        {f.familyLabel?.trim() && f.categoryName?.trim() ? (
                          <span className="mt-0.5 block text-xs font-normal text-neutral-500">{f.categoryName}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-middle font-mono text-xs text-neutral-600">{f.id}</td>
                      <td className="px-3 py-2 align-middle text-neutral-600">{f.variants?.length ?? 0}</td>
                      <td className="whitespace-nowrap px-3 py-2 align-middle text-right">
                        <div className="inline-flex flex-wrap justify-end gap-2">
                          <Link
                            to={`/admin/dashboard/seed-varieties/products/${encodeURIComponent(f.id)}/edit`}
                            className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            disabled={busy}
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                            onClick={() => removeProduct(f)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
